import type { ArchiveFilters, FtmPublicSnapshot, ReportFilters } from "../types";
import type { ArchiveEntry, Report } from "../types";
import type { CursorPage, PublicPageRequest } from "../publication/types";
import type { FtmDataProvider, FtmDataService } from "./ftmDataProvider";

function matchesSearch(value: string, search: string | undefined): boolean {
  if (!search?.trim()) return true;
  return value.toLowerCase().includes(search.trim().toLowerCase());
}

function reportMatches(report: Awaited<ReturnType<FtmDataProvider["getCurrentReports"]>>[number], filters: ReportFilters = {}): boolean {
  if (filters.category && report.category !== filters.category) return false;
  if (filters.state && report.state !== filters.state) return false;
  return matchesSearch(`${report.title} ${report.category ?? ""} ${report.platforms.map((platform) => platform.name).join(" ")}`, filters.search);
}

function onePage<T>(items: T[], limit?: number): CursorPage<T> {
  return { items, page: { limit: limit ?? items.length, nextCursor: null, hasMore: false } };
}

export function createFtmDataService(provider: FtmDataProvider): FtmDataService {
  return {
    async loadDiscover(): Promise<FtmPublicSnapshot> {
      const [markets, reports, archive, systemStatus] = await Promise.allSettled([
        provider.getMarketSnapshot(),
        provider.getCurrentReports(),
        provider.getArchive(),
        provider.getSystemStatus(),
      ]);
      const [recentIntelligence, marketCards, estimateHistories, estimateRevisions, publicationManifest] = await Promise.all([
        provider.getRecentIntelligence?.() ?? Promise.resolve([]),
        provider.getMarketCards?.() ?? Promise.resolve([]),
        provider.getEstimateHistories?.() ?? Promise.resolve([]),
        provider.getEstimateRevisions?.() ?? Promise.resolve([]),
        provider.getPublicationManifest?.() ?? Promise.resolve(null),
      ]);
      const errors: string[] = [];
      const value = <T>(result: PromiseSettledResult<T>, name: string, fallback: T): T => {
        if (result.status === "fulfilled") return result.value;
        errors.push(`${name} unavailable`);
        return fallback;
      };
      return {
        marketSnapshot: value(markets, "Market snapshot", { markets: [] }),
        currentReports: value(reports, "Current reports", []),
        archive: value(archive, "Report archive", []),
        systemStatus: value(systemStatus, "System status", {}),
        errors,
        recentIntelligence,
        marketCards,
        estimateHistories,
        estimateRevisions,
        publicationManifest,
      };
    },

    async listReports(filters: ReportFilters = {}) {
      const reports = await provider.getCurrentReports();
      return reports.filter((report) => reportMatches(report, filters));
    },

    async listReportsPage(request: PublicPageRequest = {}) {
      if (provider.getCurrentReportsPage) return provider.getCurrentReportsPage(request);
      const reports = await provider.getCurrentReports();
      return onePage(reports.filter((report) => reportMatches(report, request)), request.limit);
    },

    getReport(slug: string) {
      return provider.getReport(slug);
    },

    async listArchive(filters: ArchiveFilters = {}) {
      const archive = await provider.getArchive();
      return archive.filter((entry) => reportMatches(entry.report, filters));
    },

    async listArchivePage(request: PublicPageRequest = {}) {
      if (provider.getArchivePage) return provider.getArchivePage(request);
      const archive = await provider.getArchive();
      return onePage(archive.filter((entry) => reportMatches(entry.report, request)), request.limit);
    },

    getSystemStatus() {
      return provider.getSystemStatus();
    },
  };
}
